import {
  Box,
  BoxProps,
  Flex,
  Icon,
  LinkBox,
  LinkOverlay,
  Text,
} from '@chakra-ui/react';
import {ArrowForwardIcon} from '@chakra-ui/icons';
import {useTranslation} from 'react-i18next';
import {
  RATIO_CADRAGE,
  contextToDepuis,
  formatDateForDisplay,
  routes,
  urlImageBandeau,
} from 'ff-common';
import {BanniereCadrage} from 'api-types/ffconnect';
import {OperationsCommercialesListeViewContext} from '@/domains/operation_commerciale/OperationCommercialeListeView.tsx';
import {useNavigate} from 'react-router-dom';
import {HStack} from '@chakra-ui/layout';
import {useContext} from 'react';
import {BreakPointContext} from '@/chakra/BreakpointProvider';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {useCapture} from '@/CaptureSetup';
import {imageOptimisee} from '@/domains/mediatheque/optimisation';

interface OperationCommercialeListeItemProps {
  isLink?: boolean;
  id_operation_commerciale: string;
  nom: string;
  description: string;
  banniere_url: string;
  // Image propre au rendu mobile ; absente, `banniere_url` sert aussi en mobile.
  banniere_url_mobile?: string;
  // Couleur du bandeau choisie au backoffice ; absente, la couleur du thème.
  banniere_couleur?: string;
  // Cadrage des images choisi au backoffice ; absent, chaque image est affichée entière.
  banniere_cadrage?: BanniereCadrage;
  context?: OperationsCommercialesListeViewContext | 'liste_detail';
  onRedirect?: () => void;
  date_fin?: string | undefined;
  affiche_date_fin?: boolean;
}

type ContexteBandeau = NonNullable<
  OperationCommercialeListeItemProps['context']
>;

// Le bandeau a le même rendu partout, au ratio du cadrage de son image ; seuls les éléments
// affichés et la taille du texte changent d'un écran à l'autre.
interface FormatBandeau {
  rayon: BoxProps['borderRadius'];
  // Marge à gauche du texte
  gauche: string;
  taille_titre: string;
  avec_badge: boolean;
  avec_description: boolean;
  // « Découvrir », affiché seulement si le bandeau est cliquable
  avec_lien: boolean;
  // Largeur minimale pour le défilement horizontal
  largeur_min?: string;
  // Largeur demandée à Cloudimage pour l'image ordinateur : le double de la plus grande largeur
  // affichée, pour rester nette sur les écrans haute densité.
  largeur_image: number;
}

const FORMAT_PAR_CONTEXTE: Record<ContexteBandeau, FormatBandeau> = {
  liste: {
    rayon: '16px',
    gauche: '39px',
    taille_titre: '25px',
    avec_badge: true,
    avec_description: true,
    avec_lien: true,
    largeur_image: 2300,
  },
  liste_detail: {
    // Bord à bord sur téléphone
    rayon: {base: '0', md: '16px'},
    gauche: '39px',
    taille_titre: '25px',
    avec_badge: true,
    avec_description: true,
    avec_lien: false,
    largeur_image: 2300,
  },
  menu_catalogue: {
    rayon: '16px',
    gauche: '39px',
    taille_titre: '25px',
    avec_badge: true,
    avec_description: true,
    avec_lien: true,
    largeur_image: 1280,
  },
  accueil: {
    rayon: '16px',
    gauche: '24px',
    taille_titre: '25px',
    avec_badge: true,
    avec_description: false,
    avec_lien: true,
    largeur_image: 1100,
  },
  search_sidebar: {
    rayon: '12px',
    gauche: '25px',
    taille_titre: '16px',
    avec_badge: false,
    avec_description: false,
    avec_lien: false,
    largeur_image: 640,
  },
  search_bar: {
    rayon: '12px',
    gauche: '25px',
    taille_titre: '16px',
    avec_badge: true,
    avec_description: false,
    avec_lien: false,
    largeur_min: '280px',
    largeur_image: 900,
  },
};

// Largeur demandée à Cloudimage pour l'image mobile : 430 px au plus sur un téléphone, doublés.
const LARGEUR_IMAGE_MOBILE = 900;
// Le texte reste sur la partie la plus colorée du dégradé.
const LARGEUR_MAX_TEXTE = '482px';

/**
 * Bandeau d'une opération commerciale côté client : liste « En ce moment », page détail, accueil,
 * recherche et menu catalogue. Les aperçus du backoffice rendent ce même composant.
 */
export const OperationCommercialeCoverItem = ({
  isLink = true,
  id_operation_commerciale,
  context,
  nom,
  description,
  banniere_url,
  banniere_url_mobile,
  banniere_couleur,
  banniere_cadrage,
  onRedirect,
  date_fin,
  affiche_date_fin,
}: OperationCommercialeListeItemProps) => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();
  const breakpointContext = useContext(BreakPointContext);
  const isMobile = breakpointContext?.isMobile ?? false;

  // Format lu dans le contexte, pas par une prop responsive : l'aperçu « Mobile » du backoffice
  // le force alors que l'écran est celui d'un ordinateur.
  const format_image = isMobile ? 'mobile' : 'ordinateur';
  const url_image_cadree = urlImageBandeau(format_image, {
    banniere_url,
    banniere_url_mobile,
    banniere_cadrage,
  });
  const couleur_bandeau = banniere_couleur ?? 'blue.main';
  const capture = useCapture();

  // `?? 'liste'` plutôt qu'une valeur par défaut sur la prop, qui changerait aussi le suivi des clics.
  const format = FORMAT_PAR_CONTEXTE[context ?? 'liste'];
  const largeur_image = isMobile ? LARGEUR_IMAGE_MOBILE : format.largeur_image;
  const taille_titre = isMobile ? '17px' : format.taille_titre;

  const body = (
    // Fond bleu clair recouvert par l'image qui se voit que sans image,
    // pendant son chargement, ou si elle ne charge pas.
    // Hauteur déduite de la largeur, au ratio du cadrage : le client voit en entier le rectangle
    // dessiné au backoffice, sur tous les écrans. `clip` et non `hidden` : un conteneur `hidden`
    // ne grandit jamais, alors qu'avec `clip` le bandeau s'allonge si son texte ne tient pas.
    <Flex
      position='relative'
      direction='column'
      justify='center'
      overflow='clip'
      aspectRatio={RATIO_CADRAGE[format_image]}
      // Dans une rangée, remplit la hauteur du plus grand voisin.
      flexGrow={1}
      width='100%'
      borderRadius={format.rayon}
      bg='blue.200'
    >
      {url_image_cadree && (
        <Box
          position='absolute'
          inset='0'
          backgroundImage={`url("${imageOptimisee(url_image_cadree, largeur_image, {sans_agrandir: true})}")`}
          backgroundSize='cover'
          backgroundPosition='center'
        />
      )}
      {/* Calque en dégradé : dans le même backgroundImage que l'image, la virgule
          entre les deux ferait lire la valeur comme un dégradé Chakra, et l'invaliderait. */}
      <Box
        position='absolute'
        inset='0'
        bgGradient={`linear(to-r, ${couleur_bandeau}, transparent 80%)`}
      />
      {/* Positionné pour être peint au-dessus des deux calques en position absolue. */}
      <Flex
        position='relative'
        direction='column'
        align='flex-start'
        gap='12px'
        pl={isMobile ? '16px' : format.gauche}
        pr='16px'
        py='16px'
        color='white'
      >
        {format.avec_badge && affiche_date_fin && date_fin && (
          <Flex
            align='center'
            height='36px'
            px='12px'
            py='8px'
            borderRadius='full'
            bg='white.transparency_20'
            backdropFilter='blur(8px)'
          >
            {/* Badge indiquant la date de fin de l'opération commerciale */}
            <Text
              fontSize='13px'
              fontWeight='600'
              whiteSpace='nowrap'
              mb='0'
            >
              {t('operation_commerciale.jusqu_au', {
                date: formatDateForDisplay(date_fin, 'd MMMM yyyy'),
              })}
            </Text>
          </Flex>
        )}
        {/* Nom de l'opération commerciale */}
        <Text
          fontSize={taille_titre}
          fontWeight='700'
          lineHeight='normal'
          noOfLines={isMobile ? 2 : 1}
          maxW={LARGEUR_MAX_TEXTE}
          mb='0'
        >
          {nom}
        </Text>
        {/* Description de l'opération commerciale */}
        {format.avec_description && (
          <Text
            fontSize='14px'
            fontWeight='600'
            lineHeight='1.4'
            noOfLines={2}
            maxW={LARGEUR_MAX_TEXTE}
            mb='0'
          >
            {description}
          </Text>
        )}
        {/* Lien "Découvrir" vers les détails de l'opération commerciale */}
        {isLink && format.avec_lien && (
          <HStack spacing='5px'>
            <Text
              textDecoration='underline'
              fontWeight='600'
              fontSize='14px'
              mb='0'
            >
              {t('operation_commerciale.voir_details')}
            </Text>
            <Icon
              as={ArrowForwardIcon}
              boxSize='19px'
            />
          </HStack>
        )}
      </Flex>
    </Flex>
  );

  const handleOnClick = () => {
    const depuis = contextToDepuis(context);

    // Tracker l'événement de navigation
    capture({
      name: 'operation_commerciale:go',
      props: {
        depuis,
        id_operation_commerciale,
        nom_operation_commerciale: nom,
      },
    });

    const href = buildClientRoute(
      routes.client.mes_produits_operation_commerciale_detail,
      {id_operation_commerciale},
    );
    navigate(href);
    if (onRedirect) {
      onRedirect();
    }
  };

  return (
    <>
      {isLink ? (
        <LinkBox
          flex='1'
          minWidth={format.largeur_min}
          display='flex'
          flexDirection='column'
        >
          <LinkOverlay
            _hover={{cursor: 'pointer'}}
            onClick={handleOnClick}
            display='flex'
            flexDirection='column'
            flexGrow={1}
          >
            {body}
          </LinkOverlay>
        </LinkBox>
      ) : (
        body
      )}
    </>
  );
};
